import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ViewTypeName } from "@air/api/types";
import { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PublicBoardHeader } from "~/_app/(public-board)/_components/PublicBoardHeader";
import { PublicBoardStatsBar } from "~/_app/(public-board)/_components/PublicBoardStatsBar";
import { BOARD_STATS_BAR_HEIGHT } from "~/_components/BoardStatsBar";
import { PublicBoardCustomFieldsPanel } from "~/components/PublicBoard/components/PublicBoardCustomFieldsPanelContent/PublicBoardCustomFieldsPanel";
import { PublicBoardGalleryView } from "~/components/PublicBoard/PublicBoardGalleryView";
import { PublicBoardTableView } from "~/components/PublicBoard/PublicBoardTableView";
import { PublicBoardsAndAssetsSelectionBar } from "~/components/PublicGallery/PublicBoardsAndAssetsSelectionBar";
import { usePublicPermissions } from "~/components/PublicPermissionsProvider";
import { useIsSearchActive } from "~/hooks/search/useIsSearchActive";
import { useRerenderOnMount } from "~/hooks/useRerenderOnMount";
import { CentralizedBoardSelectors } from "~/store/centralizedBoard/selectors";
import { setConfigurableViewFieldsAction } from "~/store/configViews/actions";
import { currentViewTypeNameSelector } from "~/store/configViews/selectors";
import { useGetPublicConfigViewOptions } from "~/swr-hooks/gallery/viewFields/useGetPublicConfigViewsOptions";
import { canSeeBoardCustomFields } from "~/utils/permissions/boardPermissions";
import { PublicKanbanView } from "./components/PublicKanbanView/PublicKanbanView";
import { PublicBoardViewControls } from "./PublicBoardViewControls";
export const PublicBoardView = /*#__PURE__*/ memo((param)=>{
    let { scrollElementRef, headerRef } = param;
    const boardId = useSelector(CentralizedBoardSelectors.boardId);
    const { permissions } = usePublicPermissions();
    const canViewCustomFields = canSeeBoardCustomFields(permissions);
    const currentViewTypeName = useSelector(currentViewTypeNameSelector);
    const dispatch = useDispatch();
    const { data: configViewOptions } = useGetPublicConfigViewOptions();
    const { isSearchActive } = useIsSearchActive();
    useEffect(()=>{
        if (configViewOptions === null || configViewOptions === void 0 ? void 0 : configViewOptions.data) {
            dispatch(setConfigurableViewFieldsAction({
                configurableViewFields: configViewOptions === null || configViewOptions === void 0 ? void 0 : configViewOptions.data
            }));
        }
    }, [
        configViewOptions === null || configViewOptions === void 0 ? void 0 : configViewOptions.data,
        dispatch
    ]);
    /**
   * In order to prevent a rehydration issue, we force this page to re-render once after mount.
   * The scenario we're preventing for is that when a user goes directly to a search result url
   * for a public board, on the server side, isSearchActive is false but on first render client side,
   * it's true. So we need to use isMounted checks and also ensure there are 2 renders to fix this.
   */ const { isMounted } = useRerenderOnMount();
    if (!boardId) return null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grow",
                style: {
                    height: "calc(100% - ".concat(BOARD_STATS_BAR_HEIGHT, "px)")
                },
                children: [
                    !isSearchActive && isMounted && /*#__PURE__*/ _jsx(PublicBoardHeader, {}),
                    /*#__PURE__*/ _jsx(PublicBoardViewControls, {
                        headerRef: headerRef,
                        scrollRef: scrollElementRef
                    }),
                    currentViewTypeName === ViewTypeName.gallery && /*#__PURE__*/ _jsx(PublicBoardGalleryView, {
                        scrollElementRef: scrollElementRef
                    }),
                    currentViewTypeName === ViewTypeName.table && /*#__PURE__*/ _jsx(PublicBoardTableView, {
                        scrollElementRef: scrollElementRef
                    }),
                    currentViewTypeName === ViewTypeName.kanban && /*#__PURE__*/ _jsx(PublicKanbanView, {}),
                    /*#__PURE__*/ _jsx(PublicBoardsAndAssetsSelectionBar, {})
                ]
            }),
            /*#__PURE__*/ _jsx(PublicBoardStatsBar, {
                boardId: boardId
            }),
            canViewCustomFields && /*#__PURE__*/ _jsx(PublicBoardCustomFieldsPanel, {})
        ]
    });
});
PublicBoardView.displayName = "PublicBoardView";
